import React, { useState, useEffect } from "react";
import { DoubleRightOutlined, DoubleLeftOutlined } from "@ant-design/icons";
import { Col } from "antd";
import { CustomSider, SIDEBAR_WIDTH, ChatSiderMenuStyle, LoadMoreButtonStyle, CollapsibleButton } from "./Styles";
import { getUserRecentAIChatBotConversation as getUserRecentAIChatBotConversationAPI } from "../../api/api";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../hooks/fetch";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";
import { DEFAULT_RECENT_CONVERSATIONS, MAX_RECENT_CONVERSATIONS, PENDO_EVENTS } from "./Constants";
import { ChatHeader } from "./ChatHeader";

const ChatSider = ({
  collapsed,
  onCollapse,
  userId,
  activeConversationId,
  handleConversationChange,
  shouldRefreshConversationList,
  onConversationListRefreshed,
  headerProps
}) => {
  const { trackEvent } = useDashboardBucketsTrackingEvent();
  const [userRecentConversations, setUserRecentConversations] = useState(null);
  const [loadMore, setLoadMore] = useState(false);

  const { t } = useTranslation();

  const [getUserRecentConversations, userRecentConversationResult, , userRecentConversationError] = useFetch(
    getUserRecentAIChatBotConversationAPI
  );

  useEffect(() => {
    if (userId && (!userRecentConversations || shouldRefreshConversationList)) {
      getUserRecentConversations(userId, DEFAULT_RECENT_CONVERSATIONS);
    }
  }, [userId, shouldRefreshConversationList]);

  useEffect(() => {
    if (userRecentConversationError) {
      ErrorMessage(t("ai_chat_bot.errors.failed_fetching_recent_conversations"));
    }
  }, [userRecentConversationError]);

  useEffect(() => {
    if (userRecentConversationResult) {
      const recentConversations = userRecentConversationResult?.data;
      if (recentConversations) {
        const items = [
          {
            key: "recent",
            label: t("ai_chat_bot.recent_conversations"),
            type: "group",
            children: recentConversations.map((conversation) => ({
              key: conversation.id,
              label: conversation.topic || t("ai_chat_bot.new_chat")
            }))
          }
        ];
        setUserRecentConversations(items);
      }
    }
  }, [userRecentConversationResult]);

  useEffect(() => {
    if (shouldRefreshConversationList && onConversationListRefreshed) {
      onConversationListRefreshed();
    }
  }, [shouldRefreshConversationList, onConversationListRefreshed]);

  const handleLoadMore = () => {
    trackEvent(PENDO_EVENTS.RECENT_CHATS_LOAD_MORE);
    setLoadMore(true);
    getUserRecentConversations(userId, MAX_RECENT_CONVERSATIONS);
  };

  return (
    <>
      <CustomSider {...{ collapsed, onCollapse }} collapsedWidth={0} width={SIDEBAR_WIDTH}>
        {!collapsed && headerProps && (
          <ChatHeader
            onNewChat={headerProps.onNewChat}
            collapsed={headerProps.collapsed}
            onCollapsed={headerProps.onCollapsed}
          />
        )}
        <CollapsibleButton
          id="chat_bot_collapse_sidebar_button"
          type="text"
          icon={collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
          onClick={onCollapse}
          collapsed={collapsed}
        />
        <Col
          style={{
            bottom: "10px",
            position: "absolute",
            marginTop: "10px",
            width: "100%",
            flexDirection: "column",
            display: "flex"
          }}
        >
          <ChatSiderMenuStyle
            key={activeConversationId}
            items={userRecentConversations}
            onClick={({ key }) => handleConversationChange(key)}
            selectedKeys={activeConversationId ? [activeConversationId] : []}
            multiple={false}
          />
          {userRecentConversations && !loadMore && !collapsed && (
            <LoadMoreButtonStyle type="text" onClick={handleLoadMore}>
              {t("ai_chat_bot.load_more")}
            </LoadMoreButtonStyle>
          )}
        </Col>
      </CustomSider>
    </>
  );
};

export { ChatSider };
